"use client";

import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { getCriticalAssets } from "@/lib/critical-css";

interface PreloadResourcesProps {
  fonts?: boolean;
  images?: string[];
  scripts?: string[];
  routes?: string[];
  css?: string[];
}

export function PreloadResources({
  fonts = true,
  images = [],
  scripts = [],
  routes = [],
  css = [],
}: PreloadResourcesProps) {
  const pathname = usePathname();

  useEffect(() => {
    // Get route-specific critical assets
    const criticalAssets = getCriticalAssets(pathname || "/");

    // Combine with any additional assets passed as props
    const normalizeAssetList = (assets: string[]) =>
      Array.from(
        new Set(
          assets
            .map((asset) => asset.trim())
            .filter(
              (asset) =>
                asset.length > 0 &&
                asset !== "/" &&
                !asset.startsWith("undefined") &&
                !asset.startsWith("null"),
            ),
        ),
      );

    const allImages = normalizeAssetList([
      ...(criticalAssets.images || []),
      ...images,
    ]);
    const allCss = normalizeAssetList([...(criticalAssets.css || []), ...css]);
    const allFonts = fonts
      ? normalizeAssetList([...(criticalAssets.fonts || [])])
      : [];

    // Function to create and append preload links
    const createPreloadLink = (
      href: string,
      as: string,
      type?: string,
      crossOrigin?: string,
    ) => {
      if (!href || href.trim().length === 0) return;

      const existingLink = document.querySelector(
        `link[rel="preload"][href="${href}"]`,
      );
      if (existingLink) return; // Don't create duplicates

      const link = document.createElement("link");
      link.rel = "preload";
      link.href = href;
      link.as = as;
      if (type) link.type = type;
      if (crossOrigin) link.crossOrigin = crossOrigin;
      document.head.appendChild(link);
    };

    // Create and append prefetch links for common routes
    if (routes.length > 0) {
      routes.forEach((route) => {
        const normalizedRoute = route.trim();
        if (!normalizedRoute.startsWith("/")) return;

        if (normalizedRoute !== pathname) {
          // Don't preload current page
          const existingLink = document.querySelector(
            `link[rel="prefetch"][href="${normalizedRoute}"]`,
          );
          if (!existingLink) {
            const link = document.createElement("link");
            link.rel = "prefetch";
            link.href = normalizedRoute;
            document.head.appendChild(link);
          }
        }
      });
    }

    // Create and append preload links for critical CSS
    if (allCss.length > 0) {
      allCss.forEach((cssPath) => {
        createPreloadLink(cssPath, "style", "text/css");
      });
    }

    // Create and append preload links for critical images
    if (allImages.length > 0) {
      allImages.forEach((image) => {
        createPreloadLink(image, "image");
      });
    }

    // Create and append preload links for important scripts
    if (scripts.length > 0) {
      scripts.forEach((script) => {
        createPreloadLink(script, "script");
      });
    }

    // Preload critical fonts
    if (allFonts.length > 0) {
      allFonts.forEach((font) => {
        createPreloadLink(font, "font", "font/woff2", "anonymous");
      });
    }

    return () => {
      // Cleanup is optional since preloaded resources are beneficial
      // but we can remove links with data attribute to identify them if needed
    };
  }, [pathname, fonts, images, scripts, routes, css]);

  return null;
}
